import React, { useMemo } from "react";
import { DefaultButton, Label, Modal, Pivot, PivotItem, Spinner, SpinnerSize } from "@fluentui/react";
import { SignatureComponent } from "@/components/Signature/SignatureComponent";
import type { UserIdentityType } from "@/services/UserIdentitiesService";
import { ModalAccessRequestsProps } from "@/screen/Identities/Interfaces/IdentitiesMainScreenInterfaces";
import { accessRequestsModalContentStyles, accessRequestsModalFluentStyles, accessRequestsModalTitleLabelStyles, accessRequestsTableActionButtonCancel, accessRequestsTableActionButtonDef, ActionButtonsWrap, AccessRequestsDecisionErrorText, AccessRequestsEmptyMessage, AccessRequestsEmptyPendingMessage, AccessRequestsHistoryEmptyCell, AccessRequestsIntroText, AccessRequestsLoadingWrap, AccessRequestsModalFooter, AccessRequestsModalGlobalStyle, AccessRequestsModalScrollArea, AccessRequestsSectionTitle, AccessRequestsSignatureWrap, AccessRequestsTable, PivotWrap } from "@/screen/Identities/Styles/ModalAccessRequestsStyles";
import { styleDefButton } from "@/screen/Identities/Styles/IdentitiesMainScreenStyles";

const ModalAccessRequests: React.FC<ModalAccessRequestsProps> = ({isOpen, onClose, guestPendingRows, ownPendingRows, historyRows, accessRequestsLoading, accessRequestsLoadError, accessRequestDecisionSubmitting, accessRequestDecisionError, accessRequestDecisionRetryAvailable, accessRequestSignaturePhase, accessRequestSignatureLoading, accessRequestSignatures, accessRequestSignatureUserIdentity, accessRequestSignatureHeaderText, onAccessRequestSignatureSigned, onAccessRequestSignatureCancel, onAccessRequestSignatureNewCodeSended, onAccessRequestSignatureChanged, onRetryAccessRequestDecision, onAcceptRequest, onRejectRequest}) =>
{
    const emptyPending = useMemo(() => !accessRequestsLoading && guestPendingRows.length === 0 && ownPendingRows.length === 0 && !accessRequestsLoadError, [accessRequestsLoading, guestPendingRows.length, ownPendingRows.length, accessRequestsLoadError]);

    const actionsDisabled = accessRequestDecisionSubmitting || accessRequestSignaturePhase || accessRequestSignatureLoading;
    const footerDisabled = accessRequestDecisionSubmitting || accessRequestSignatureLoading;

    return(
        <React.Fragment>
            <AccessRequestsModalGlobalStyle />
            <Modal isOpen={isOpen} isBlocking={false} className="access-requests-modal"
                styles={accessRequestsModalFluentStyles}
            >
                <div className={accessRequestsModalContentStyles.container}>
                    <div className={accessRequestsModalContentStyles.header}>
                        <Label styles={accessRequestsModalTitleLabelStyles}>
                            Solicitudes de acceso a intervinientes
                        </Label>
                    </div>
                    <div className={accessRequestsModalContentStyles.body}>
                        {!accessRequestSignaturePhase &&
                        (
                            <AccessRequestsIntroText>
                                Aquí se muestran todas las solicitudes de acceso a intervinientes de tu usuario.
                                Revisa todas las solicitudes entrantes y el histórico de peticiones recibidas.
                                Al aceptar o rechazar una solicitud, informaremos al usuario que te lo solicitó con tu decisión.
                            </AccessRequestsIntroText>
                        )}
                        {accessRequestDecisionError &&
                        (
                            <AccessRequestsDecisionErrorText role="alert">
                                {accessRequestDecisionError}
                            </AccessRequestsDecisionErrorText>
                        )}
                        {accessRequestDecisionRetryAvailable && !accessRequestSignaturePhase &&
                        (
                            <ActionButtonsWrap>
                                <DefaultButton styles={styleDefButton} onClick={onRetryAccessRequestDecision} disabled={accessRequestDecisionSubmitting}>
                                    Reintentar
                                </DefaultButton>
                            </ActionButtonsWrap>
                        )}
                        {accessRequestsLoading &&
                        (
                            <AccessRequestsLoadingWrap>
                                <Spinner size={SpinnerSize.large} label="Cargando solicitudes..." />
                            </AccessRequestsLoadingWrap>
                        )}
                        {!accessRequestsLoading && accessRequestsLoadError &&
                        (
                            <AccessRequestsEmptyMessage>
                                {accessRequestsLoadError}
                            </AccessRequestsEmptyMessage>
                        )}
                        {!accessRequestsLoading && !accessRequestsLoadError && accessRequestSignaturePhase &&
                        (
                            accessRequestSignatureLoading ?
                            (
                                <AccessRequestsLoadingWrap>
                                    <Spinner size={SpinnerSize.large} label="Preparando firma..." />
                                </AccessRequestsLoadingWrap>
                            ) : accessRequestSignatures.length > 0 && accessRequestSignatureUserIdentity ?
                            (
                                <AccessRequestsModalScrollArea>
                                    <AccessRequestsSignatureWrap>
                                        <SignatureComponent userIdentities={[accessRequestSignatureUserIdentity as UserIdentityType]}
                                            signatures={accessRequestSignatures}
                                            title="Firma la operación"
                                            submitButtonText="Firmar solicitud"
                                            cancelButtonText="Cancelar"
                                            headerTextArea={accessRequestSignatureHeaderText}
                                            alreadySignedText="Solicitud firmada con éxito."
                                            backButtonText="Volver"
                                            requestNewCodeButtonText="Recibir nuevo código"
                                            onCancelButtonClicked={onAccessRequestSignatureCancel}
                                            onBackButtonClicked={onAccessRequestSignatureCancel}
                                            onNewCodeSended={onAccessRequestSignatureNewCodeSended}
                                            onSignatureChanged={onAccessRequestSignatureChanged}
                                            onSigned={onAccessRequestSignatureSigned}
                                        />
                                    </AccessRequestsSignatureWrap>
                                </AccessRequestsModalScrollArea>
                            ) :
                            (
                                <AccessRequestsEmptyMessage>
                                    No se pudo cargar la firma. Cierre el modal e inténtelo de nuevo.
                                </AccessRequestsEmptyMessage>
                            )
                        )}
                        {!accessRequestsLoading && !accessRequestsLoadError && !accessRequestSignaturePhase &&
                        (
                        <PivotWrap>
                            <Pivot defaultSelectedKey="actuales">
                                <PivotItem headerText="Solicitudes Actuales" itemKey="actuales">
                                    {emptyPending ?
                                    (
                                        <AccessRequestsEmptyPendingMessage>
                                            No hay solicitudes pendientes.
                                        </AccessRequestsEmptyPendingMessage>
                                    ) :
                                    (
                                        <React.Fragment>
                                            {guestPendingRows.length > 0 &&
                                            (
                                                <React.Fragment>
                                                    <AccessRequestsSectionTitle>
                                                        Solicitudes recibidas
                                                    </AccessRequestsSectionTitle>
                                                    <AccessRequestsTable>
                                                        <thead>
                                                            <tr>
                                                                <th>Correo Usuario Solicitante</th>
                                                                <th>Interviniente</th>
                                                                <th>Operación</th>
                                                                <th>Fecha solicitud</th>
                                                                <th>Estado</th>
                                                                <th className="actions-cell">Acciones</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {guestPendingRows.map((row) =>
                                                            (
                                                                <tr key={row.id}>
                                                                    <td>{row.guestEmail}</td>
                                                                    <td>{row.identityName}</td>
                                                                    <td>{row.operation}</td>
                                                                    <td>{row.requestDate}</td>
                                                                    <td>{row.status}</td>
                                                                    <td className="actions-cell">
                                                                        <ActionButtonsWrap>
                                                                            <DefaultButton styles={accessRequestsTableActionButtonDef}
                                                                                disabled={actionsDisabled}
                                                                                onClick={() => void onAcceptRequest(row)}
                                                                            >
                                                                                Aceptar
                                                                            </DefaultButton>
                                                                            <DefaultButton styles={accessRequestsTableActionButtonCancel}
                                                                                disabled={actionsDisabled}
                                                                                onClick={() => void onRejectRequest(row)}
                                                                            >
                                                                                Rechazar
                                                                            </DefaultButton>
                                                                        </ActionButtonsWrap>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </AccessRequestsTable>
                                                </React.Fragment>
                                            )}
                                            {ownPendingRows.length > 0 &&
                                            (
                                                <React.Fragment>
                                                    <AccessRequestsSectionTitle>
                                                        Mis solicitudes enviadas
                                                    </AccessRequestsSectionTitle>
                                                    <AccessRequestsTable className="readonly">
                                                        <thead>
                                                            <tr>
                                                                <th>Correo Usuario Solicitado</th>
                                                                <th>Interviniente</th>
                                                                <th>Operación</th>
                                                                <th>Fecha solicitud</th>
                                                                <th>Estado</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {ownPendingRows.map((row) =>
                                                            (
                                                                <tr key={row.id}>
                                                                    <td>{row.guestEmail}</td>
                                                                    <td>{row.identityName}</td>
                                                                    <td>{row.operation}</td>
                                                                    <td>{row.requestDate}</td>
                                                                    <td>{row.status}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </AccessRequestsTable>
                                                </React.Fragment>
                                            )}
                                        </React.Fragment>
                                    )}
                                </PivotItem>
                                <PivotItem headerText="Histórico del Registro" itemKey="historico">
                                    <AccessRequestsTable className="readonly">
                                        <thead>
                                            <tr>
                                                <th>Correo Usuario Solicitante</th>
                                                <th>Interviniente</th>
                                                <th>Operación</th>
                                                <th>Fecha solicitud</th>
                                                <th>Fecha resolución</th>
                                                <th>Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyRows.length === 0 ?
                                            (
                                                <tr>
                                                    <AccessRequestsHistoryEmptyCell colSpan={6}>
                                                        No hay registros en el histórico.
                                                    </AccessRequestsHistoryEmptyCell>
                                                </tr>
                                            ) :
                                            (
                                                historyRows.map((row, index) =>
                                                (
                                                    <tr key={`${row.id}-${row.resolutionDate}-${index}`}>
                                                        <td>{row.guestEmail}</td>
                                                        <td>{row.identityName}</td>
                                                        <td>{row.operation}</td>
                                                        <td>{row.requestDate}</td>
                                                        <td>{row.resolutionDate}</td>
                                                        <td>{row.status}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </AccessRequestsTable>
                                </PivotItem>
                            </Pivot>
                        </PivotWrap>
                        )}
                    </div>
                    {!accessRequestSignaturePhase &&
                    (
                        <AccessRequestsModalFooter>
                            <DefaultButton styles={styleDefButton} onClick={onClose} disabled={footerDisabled}>
                                Cerrar
                            </DefaultButton>
                        </AccessRequestsModalFooter>
                    )}
                </div>
            </Modal>
        </React.Fragment>
    );
};

export default ModalAccessRequests;